import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, Stack, Typography, IconButton, Skeleton, Alert, Chip, Button,
} from '@mui/material';
import {
  VisibilityRounded as ShowIcon,
  VisibilityOffRounded as HideIcon,
  ChevronRightRounded as ChevronIcon,
  AccountBalanceWalletRounded as AccountIcon,
  SchoolRounded as AcademicIcon,
  PaymentsRounded as FinanceIcon,
  PersonRounded as ProfileIcon,
  FactCheckRounded as AttendanceIcon,
  HistoryRounded as HistoryIcon,
  ReceiptLongRounded as PaymentIcon,
  HowToRegRounded as RegisterIcon,
  RestaurantRounded as ProgrammeIcon,
  DescriptionRounded as BillIcon,
  SmartphoneRounded as PayIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import API from '../BaseURL';
import PortalPayDialog from './PortalPayDialog';
import {
  P, CARD_PATTERN, money,
  HERO_BG, HERO_SHADOW, CARD_SHADOW, CARD_SHADOW_LIFT,
} from './portalTheme';

// ─────────────────────────────────────────────────────────────────
// PortalHome — what a parent sees first.
//
// TWO SHAPES, ONE SCREEN
//   On a phone the balance cards are a carousel: there is only room
//   for one, so tapping moves to the next. On a laptop there is room
//   for all of them at once, so they simply sit side by side — a
//   carousel on a wide screen hides information for no reason. Both
//   fall out of one CSS grid rather than two code paths.
//
// PAYMENTS ARE A SUMMARY HERE, NOT A LEDGER
//   The first version listed every recent receipt down the front page,
//   which turned the screen a parent glances at into something they
//   have to scroll past. Home now shows what was paid this term and
//   the last two receipts; the full history, with the detail of each
//   one, lives behind Finance.
// ─────────────────────────────────────────────────────────────────

const QUICK = [
  { key: 'account',   label: 'Account',  icon: <AccountIcon />,  path: '/portal/account',
    blurb: 'Programmes and balances' },
  { key: 'academics', label: 'Academic', icon: <AcademicIcon />, path: '/portal/academics',
    blurb: 'Results and attendance' },
  { key: 'finance',   label: 'Finance',  icon: <FinanceIcon />,  path: '/portal/finance',
    blurb: 'Payment history' },
  { key: 'profile',   label: 'Profile',  icon: <ProfileIcon />,  path: '/portal/profile',
    blurb: 'Your details' },
];

// ── The one card ────────────────────────────────────────────────
//
// The reference app leads with a single account balance. A school is
// not one account — a learner carries School Fees, PTA Dues, Other
// Payment and sometimes a daily feeding or transport tab. So the card
// leads with the SUM of all of it, and the accounts that make up that
// sum are listed underneath, inside the same blue panel.
//
// That ordering is the point. A parent wants "what do I owe" answered
// in one number before they are shown four. The breakdown is there so
// the number can be trusted, not so it has to be added up.
// One icon per kind of thing that can happen to a child's record, and
// one colour per outcome. Kept as lookups rather than a chain of
// ternaries in the row, so adding a fifth source later is one entry.
const KIND = {
  payment:    { icon: <PaymentIcon   sx={{ fontSize: 20 }} /> },
  attendance: { icon: <RegisterIcon  sx={{ fontSize: 20 }} /> },
  programme:  { icon: <ProgrammeIcon sx={{ fontSize: 20 }} /> },
  bill:       { icon: <BillIcon      sx={{ fontSize: 20 }} /> },
};

const TONE = {
  good: { bg: '#ECFDF3', fg: '#15803D' },
  bad:  { bg: '#FEF2F2', fg: '#B91C1C' },
  warn: { bg: '#FFF7E6', fg: '#B45309' },
  flat: { bg: '#F3F4F6', fg: '#4B5563' },
};

function BalanceCard({ summary, lines, studentInd, hidden, onToggleHide, onAll, onPay }) {
  const owing = Number(summary?.owing || 0);
  // Positive = owing, negative = the parent is in credit.
  const settled = Math.abs(owing) < 0.005;
  const toneColor = owing > 0 ? P.onBrandBad : settled ? P.onBrand : P.onBrandGood;
  const word = owing > 0 ? 'total owing' : settled ? 'nothing owing' : 'in credit';

  return (
    <Card sx={{
      position: 'relative', overflow: 'hidden',
      borderRadius: 4.5, p: { xs: 2.4, md: 3 },
      // Pattern first, then the gradient underneath it — a flat fill at
      // this saturation reads as a swatch, and this card is the one
      // object on the page that should feel like an object.
      backgroundImage: `${CARD_PATTERN}, ${HERO_BG}`,
      boxShadow: HERO_SHADOW,
      // A hairline of light along the top edge, the way a raised
      // surface catches it.
      '&::after': {
        content: '""', position: 'absolute', inset: 0, borderRadius: 'inherit',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,.45)',
        pointerEvents: 'none',
      },
    }}>
      <Stack direction="row" alignItems="flex-start">
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{
            fontSize: 12, fontWeight: 800, letterSpacing: .7,
            textTransform: 'uppercase', color: P.onBrandSoft,
          }}>
            Account Balance
          </Typography>
          <Typography sx={{
            fontSize: 13, color: P.onBrandSoft, mt: .1,
            fontVariantNumeric: 'tabular-nums',
          }}>
            {studentInd || '—'}
          </Typography>
        </Box>
        <IconButton onClick={onToggleHide} sx={{ color: P.onBrand, mt: -0.8, mr: -0.8 }}>
          {hidden ? <HideIcon /> : <ShowIcon />}
        </IconButton>
      </Stack>

      <Box sx={{ mt: 1.4 }}>
        {hidden ? (
          <Typography sx={{ fontSize: 34, fontWeight: 900, color: P.onBrand, letterSpacing: 5 }}>
            ••••••
          </Typography>
        ) : (
          <>
            <Typography sx={{
              fontSize: { xs: 34, md: 38 }, fontWeight: 900, color: toneColor, lineHeight: 1.1,
              fontVariantNumeric: 'tabular-nums',
            }}>
              GHS {money(Math.abs(owing))}
            </Typography>
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: P.onBrandSoft, mt: .2 }}>
              {word}
              {summary?.accounts_owing > 0 &&
                ` · ${summary.accounts_owing} of ${lines.length} account${lines.length === 1 ? '' : 's'}`}
            </Typography>
          </>
        )}
      </Box>

      {/* The accounts behind the number, on the card. Carried through
          the hide toggle: hiding the total and leaving the parts
          visible would defeat the point of hiding it. */}
      {!hidden && lines.length > 0 && (
        <Box sx={{
          mt: 2.2, pt: 1.8,
          borderTop: '1px solid rgba(255,255,255,.20)',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: lines.length > 4 ? '1fr 1fr' : '1fr' },
          columnGap: 3, rowGap: .2,
        }}>
          {lines.map(l => {
            const owed = Number(l.balance || 0);
            const clear = Math.abs(owed) < 0.005;
            return (
              <Stack key={l.key} direction="row" alignItems="center" spacing={1.2}
                sx={{
                  py: .6, px: 1, mx: -1, borderRadius: 1.5,
                  transition: 'background-color .15s',
                  '&:hover': { bgcolor: 'rgba(255,255,255,.07)' },
                }}>
                {/* A dot per account: settled reads as calm, owing reads
                    as needing something. Colour on the figure alone is
                    easy to miss when every figure is 0.00. */}
                <Box sx={{
                  width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
                  bgcolor: clear ? 'rgba(255,255,255,.42)'
                    : owed > 0 ? P.onBrandBad : P.onBrandGood,
                }} />
                <Typography noWrap sx={{
                  flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600,
                  color: 'rgba(255,255,255,.90)',
                }}>
                  {l.label}
                  {l.sub && (
                    <Typography component="span" sx={{ fontSize: 11, color: P.onBrandFaint, ml: .8 }}>
                      {l.sub}
                    </Typography>
                  )}
                </Typography>
                <Typography sx={{
                  fontSize: 13.5, fontWeight: 800, fontVariantNumeric: 'tabular-nums',
                  color: clear ? 'rgba(255,255,255,.72)'
                    : owed > 0 ? P.onBrandBad : P.onBrandGood,
                }}>
                  {money(Math.abs(owed))}
                </Typography>
              </Stack>
            );
          })}
        </Box>
      )}

      {/* A real button, not a line of text pretending to be one.
          Paying sits on the LEFT and is solid white — it is the one
          thing a parent looking at an amount owed actually wants to
          do, and it should not have to compete with a navigation
          link for attention. */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 2 }}>
        {onPay && (
          <Stack direction="row" alignItems="center" spacing={.8}
            onClick={onPay}
            sx={{
              px: 1.9, py: .7, borderRadius: 5, cursor: 'pointer',
              bgcolor: P.onBrand,
              transition: 'transform .18s, box-shadow .18s',
              boxShadow: '0 2px 10px -2px rgba(0,0,0,.28)',
              '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 6px 16px -4px rgba(0,0,0,.34)' },
            }}>
            <PayIcon sx={{ fontSize: 17, color: P.brandDeep }} />
            <Typography sx={{ fontSize: 13, fontWeight: 900, color: P.brandDeep }}>
              Pay now
            </Typography>
          </Stack>
        )}
        <Box sx={{ flex: 1 }} />
        <Stack direction="row" alignItems="center" spacing={.8}
          onClick={onAll}
          sx={{
            pl: 1.8, pr: .6, py: .6, borderRadius: 5, cursor: 'pointer',
            bgcolor: 'rgba(255,255,255,.14)',
            border: '1px solid rgba(255,255,255,.24)',
            transition: 'background-color .18s',
            '&:hover': { bgcolor: 'rgba(255,255,255,.24)' },
          }}>
          <Typography sx={{ fontSize: 13, fontWeight: 800, color: P.onBrand }}>
            All accounts
          </Typography>
          <Box sx={{
            width: 24, height: 24, borderRadius: '50%', bgcolor: 'rgba(255,255,255,.24)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: P.onBrand,
          }}>
            <ChevronIcon sx={{ fontSize: 17 }} />
          </Box>
        </Stack>
      </Stack>
    </Card>
  );
}

// A tile is a BUTTON, so it has to look like one you can press.
//
// The first version was a very rounded box with an icon and two lines
// of text floating in the middle of it — at four-across on a laptop
// that left a wide empty right-hand side and read as a pill someone
// forgot to fill. A tighter corner, a chevron parked on the right edge
// and a hairline that warms on hover give it an obvious near side and
// far side.
function QuickTile({ icon, label, blurb, onClick }) {
  return (
    <Card onClick={onClick} sx={{
      borderRadius: 2.5, px: { xs: 1.8, md: 2 }, py: { xs: 1.6, md: 1.9 },
      cursor: 'pointer', height: '100%', position: 'relative', overflow: 'hidden',
      border: '1px solid', borderColor: P.line,
      boxShadow: CARD_SHADOW,
      display: 'flex', alignItems: 'center', gap: 1.5,
      transition: 'border-color .18s, transform .18s, box-shadow .18s',
      // A brand rule down the leading edge, revealed on hover. Cheaper
      // to read than a colour change across the whole card.
      '&::before': {
        content: '""', position: 'absolute', left: 0, top: 0, bottom: 0, width: 3,
        bgcolor: P.brand, opacity: 0, transition: 'opacity .18s',
      },
      '&:hover': {
        borderColor: 'rgba(21,101,192,.45)',
        transform: 'translateY(-3px)',
        boxShadow: CARD_SHADOW_LIFT,
        '&::before': { opacity: 1 },
        '& .tile-chevron': { opacity: 1, transform: 'translateX(0)' },
      },
    }}>
      <Box sx={{
        width: 42, height: 42, borderRadius: 2, flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: `linear-gradient(140deg, ${P.brandSoft}, ${P.brandFaint})`,
        border: '1px solid', borderColor: 'rgba(21,101,192,.20)',
        color: P.brand,
      }}>
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography noWrap sx={{ fontSize: 14.5, fontWeight: 800, color: P.ink, lineHeight: 1.3 }}>
          {label}
        </Typography>
        <Typography noWrap sx={{ fontSize: 11.5, color: P.inkSoft, display: { xs: 'none', sm: 'block' } }}>
          {blurb}
        </Typography>
      </Box>
      <ChevronIcon className="tile-chevron" sx={{
        fontSize: 20, color: P.brand, flexShrink: 0,
        opacity: 0, transform: 'translateX(-4px)',
        transition: 'opacity .18s, transform .18s',
      }} />
    </Card>
  );
}

// Section headings get a short brand rule to their left. Plain bold
// text on a plain ground gave the page no rhythm — nothing marked
// where one block ended and the next began.
function SectionTitle({ children, action, onAction, sx }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1.2} sx={{ mb: 1.4, mt: 3.5, ...sx }}>
      <Box sx={{ width: 3, height: 18, borderRadius: 2, bgcolor: P.brand, flexShrink: 0 }} />
      <Typography sx={{
        flex: 1, fontSize: { xs: 16.5, md: 17.5 }, fontWeight: 800, color: P.ink,
        letterSpacing: -.2,
      }}>
        {children}
      </Typography>
      {action && (
        <Button onClick={onAction} endIcon={<ChevronIcon />} size="small"
          sx={{
            fontWeight: 700, color: P.brand, textTransform: 'none', borderRadius: 2,
            '&:hover': { bgcolor: P.brandFaint },
          }}>
          {action}
        </Button>
      )}
    </Stack>
  );
}

// Activity is grouped under date headings rather than run as one flat
// list. Twenty undifferentiated rows is a wall; "Today", "This week",
// "June 2026" gives the eye somewhere to stop, and answers "is this
// recent?" without reading a single date.
function activityGroupLabel(iso) {
  if (!iso) return 'Earlier';
  const [y, m, d] = String(iso).split('-').map(Number);
  if (!y || !m || !d) return 'Earlier';
  const then = new Date(y, m - 1, d);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const days = Math.round((today - then) / 86400000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return 'This week';
  if (days < 30) return 'This month';
  return then.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

function groupActivity(list) {
  const out = [];
  let current = null;
  for (const a of list) {
    const label = activityGroupLabel(a.date);
    if (!current || current.label !== label) {
      current = { label, items: [] };
      out.push(current);
    }
    current.items.push(a);
  }
  return out;
}

export default function PortalHome() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [activity, setActivity]     = useState([]);
  const [actLoading, setActLoading] = useState(true);
  const [actShown, setActShown]     = useState(8);
  const [payOpen, setPayOpen] = useState(false);
  const [hidden, setHidden]   = useState(() => {
    try { return localStorage.getItem('portal_hide_balance') === '1'; } catch { return false; }
  });

  useEffect(() => {
    let alive = true;
    API.get('/portal/me')
      .then(r => { if (alive) { setData(r.data); setError(''); } })
      .catch(e => { if (alive) setError(e?.response?.data?.message || 'Could not load your account'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  // After a payment goes through, the feed is stale — the money is in
  // the approvals queue and shows as pending. Refetch rather than
  // patching state locally, so what the parent sees is what the server
  // actually holds.
  const refresh = () => {
    API.get('/portal/me').then(r => setData(r.data)).catch(() => {});
    API.get('/portal/activity', { params: { limit: 40 } })
      .then(r => setActivity(r.data?.activity || [])).catch(() => {});
  };

  // Fetched separately from the balance. It merges four tables, so it
  // is the slower of the two — letting it land on its own means the
  // balance card paints as soon as it can rather than waiting.
  useEffect(() => {
    let alive = true;
    API.get('/portal/activity', { params: { limit: 40 } })
      .then(r => { if (alive) setActivity(r.data?.activity || []); })
      .catch(() => { if (alive) setActivity([]); })
      .finally(() => { if (alive) setActLoading(false); });
    return () => { alive = false; };
  }, []);

  const toggleHide = () => setHidden(h => {
    const next = !h;
    try { localStorage.setItem('portal_hide_balance', next ? '1' : '0'); } catch { /* private mode */ }
    return next;
  });

  const student  = data?.student || { ind: user?.parent?.student_ind };

  // The breakdown printed on the card, in the order a parent thinks
  // about it: the fee accounts first (School Fees, PTA, Other), then
  // any daily programme running alongside them.
  //
  // Both are converted to ONE convention — positive means owing —
  // because they arrive with opposite ones: a fee account's `balance`
  // is due-less-paid, while a programme carries arrears and credit as
  // separate columns.
  const lines = useMemo(() => {
    const fees = (data?.fees || []).map(f => ({
      key: `fee:${f.key}`,
      label: f.label,
      balance: Number(f.balance || 0),
      sub: f.arrears > 0 ? `b/f ${money(f.arrears)}` : '',
    }));
    const progs = (data?.accounts || []).map(a => ({
      key: `sub:${a.key}`,
      label: a.label,
      balance: Number(a.arrears || 0) - Number(a.credit || 0),
      sub: a.days_owed > 0 ? `${a.days_owed}d` : '',
    }));
    return [...fees, ...progs];
  }, [data]);

  const summary = data?.summary || { owing: 0, accounts_owing: 0 };
  const hasAnything = lines.length > 0;

  return (
    <Box>
      {/* ── The balance ──────────────────────────────────────────
          One card, one total, and the accounts behind it printed on
          the same card. Not a carousel: a parent should not have to
          swipe through four cards to work out what they owe. */}
      {loading ? (
        <Skeleton variant="rounded" height={260} sx={{ borderRadius: 4 }} />
      ) : error ? (
        <Alert severity="error" sx={{ borderRadius: 3 }}>{error}</Alert>
      ) : !hasAnything ? (
        <Card sx={{
          borderRadius: 4, p: 3, textAlign: 'center',
          bgcolor: P.brandSoft, boxShadow: 'none',
          border: '1px solid', borderColor: P.line,
        }}>
          <AccountIcon sx={{ fontSize: 38, color: P.brand }} />
          <Typography sx={{ fontWeight: 800, color: P.ink, mt: .5 }}>
            No accounts yet
          </Typography>
          <Typography sx={{ fontSize: 13, color: P.inkSoft, mt: .5, maxWidth: 460, mx: 'auto' }}>
            {student?.name || 'This learner'} has no fee account assigned. The school office
            sets these up.
          </Typography>
        </Card>
      ) : (
        <BalanceCard
          summary={summary}
          lines={lines}
          studentInd={student?.ind}
          hidden={hidden}
          onToggleHide={toggleHide}
          onAll={() => nav('/portal/account')}
          onPay={() => setPayOpen(true)}
        />
      )}

      <PortalPayDialog
        open={payOpen}
        onClose={() => setPayOpen(false)}
        onQueued={refresh}
        studentName={student?.name}
      />

      {/* ── Quick Access ─────────────────────────────────────────
          Straight under the balance, full width. Two across on a
          phone, four on a laptop — the tiles get wider rather than the
          row getting taller, which is the whole reason for the extra
          screen. */}
      <SectionTitle>Quick Access</SectionTitle>
      <Box sx={{
        display: 'grid', gap: 1.4,
        gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
      }}>
        {QUICK.map(q => (
          <QuickTile key={q.key} icon={q.icon} label={q.label} blurb={q.blurb}
            onClick={() => nav(q.path)} />
        ))}
      </Box>

      {/* Attendance, as one line. It is a standing figure rather than
          an event, so it sits above the feed instead of inside it. */}
      {data?.attendance?.marked > 0 && (
        <Card sx={{
          mt: 2, borderRadius: 3.5, px: 2, py: 1.6, boxShadow: CARD_SHADOW,
          border: '1px solid', borderColor: P.line,
        }}>
          <Stack direction="row" alignItems="center" spacing={1.8}>
            <Box sx={{
              width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: P.brandSoft, color: P.brandDeep,
            }}>
              <AttendanceIcon />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 19, fontWeight: 900, color: P.ink, lineHeight: 1 }}>
                {data.attendance.rate}% attendance
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: P.inkSoft, mt: .3 }}>
                present on {data.attendance.present} of {data.attendance.marked} days marked
              </Typography>
            </Box>
            {data.attendance.absent > 0 && (
              <Chip size="small" label={`${data.attendance.absent} absent`}
                sx={{ fontWeight: 700, bgcolor: '#FEF2F2', color: P.bad }} />
            )}
          </Stack>
        </Card>
      )}

      {/* ── Recent activity ──────────────────────────────────────
          Not a payment list. Everything that has touched this child's
          record — money in, the morning register, the canteen and bus
          marks, and what the school billed — merged newest first, so a
          parent opening the app sees what has actually been happening
          rather than only what they have paid. */}
      <SectionTitle action="All payments" onAction={() => nav('/portal/finance')}>
        Recent activity
      </SectionTitle>
      {actLoading ? (
        <Stack spacing={1}>
          {[0, 1, 2, 3].map(i => (
            <Skeleton key={i} variant="rounded" height={62} sx={{ borderRadius: 3 }} />
          ))}
        </Stack>
      ) : activity.length === 0 ? (
        <Card sx={{
          borderRadius: 3, p: 3, textAlign: 'center', boxShadow: 'none',
          border: '1px dashed', borderColor: P.line,
        }}>
          <HistoryIcon sx={{ fontSize: 34, color: P.line }} />
          <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: P.ink, mt: .5 }}>
            Nothing yet
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: P.inkSoft, mt: .3 }}>
            Payments, register marks and bills will appear here as they happen.
          </Typography>
        </Card>
      ) : (
        <>
          <Card sx={{
            borderRadius: 3, boxShadow: CARD_SHADOW, overflow: 'hidden',
            border: '1px solid', borderColor: P.line,
          }}>
            {groupActivity(activity.slice(0, actShown)).map((g, gi) => (
              <Box key={g.label + gi}>
                {/* Date heading. Tinted rather than bordered, so it reads
                    as a divider in the list instead of another row. */}
                <Box sx={{
                  px: { xs: 1.8, md: 2.2 }, py: .9,
                  bgcolor: '#F7FAFE',
                  borderTop: gi === 0 ? 'none' : '1px solid',
                  borderBottom: '1px solid', borderColor: P.line,
                }}>
                  <Typography sx={{
                    fontSize: 10.5, fontWeight: 800, letterSpacing: .7,
                    textTransform: 'uppercase', color: P.inkSoft,
                  }}>
                    {g.label}
                  </Typography>
                </Box>
                {g.items.map((a, i) => {
                  const k = KIND[a.kind] || KIND.bill;
                  const tone = TONE[a.tone] || TONE.flat;
                  const credit = a.tone === 'good' && a.amount != null;
                  return (
                    <Stack key={a.id} direction="row" alignItems="center"
                      spacing={{ xs: 1.4, md: 1.8 }}
                      sx={{
                        px: { xs: 1.8, md: 2.2 }, py: 1.35,
                        borderTop: i === 0 ? 'none' : '1px solid',
                        borderColor: '#F1F5FA',
                        transition: 'background-color .15s',
                        '&:hover': { bgcolor: '#FBFDFF' },
                      }}>
                      <Box sx={{
                        width: 36, height: 36, borderRadius: 2, flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        bgcolor: tone.bg, color: tone.fg,
                      }}>
                        {k.icon}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography noWrap sx={{
                          fontSize: 13.5, fontWeight: 700, color: P.ink, lineHeight: 1.35,
                        }}>
                          {a.title}
                        </Typography>
                        <Typography noWrap sx={{ fontSize: 11.5, color: P.inkSoft }}>
                          {[a.date, a.detail].filter(Boolean).join(' · ')}
                        </Typography>
                      </Box>
                      {/* The amount column is a fixed width, so the
                          figures line up down the list instead of each
                          one ending wherever its own digits ran out. */}
                      <Box sx={{
                        width: { xs: 92, md: 116 }, flexShrink: 0, textAlign: 'right',
                      }}>
                        {a.amount != null ? (
                          <Typography sx={{
                            fontSize: 14, fontWeight: 800,
                            color: credit ? P.good : P.ink,
                            fontVariantNumeric: 'tabular-nums',
                          }}>
                            {credit ? '+' : ''}{money(a.amount)}
                          </Typography>
                        ) : (
                          // Register marks carry no money. A dash keeps
                          // the column honest rather than leaving a hole
                          // that looks like a missing figure.
                          <Typography sx={{ fontSize: 14, color: '#C9D3E0' }}>—</Typography>
                        )}
                      </Box>
                    </Stack>
                  );
                })}
              </Box>
            ))}
          </Card>
          {activity.length > actShown && (
            <Stack alignItems="center" sx={{ mt: 1.8 }}>
              <Button onClick={() => setActShown(n => n + 10)} variant="outlined"
                sx={{
                  fontWeight: 800, textTransform: 'none', borderRadius: 2.5, px: 3, py: .8,
                  borderColor: P.line, color: P.brandDeep, bgcolor: P.card,
                  '&:hover': { borderColor: P.brand, bgcolor: P.brandFaint },
                }}>
                Show {Math.min(10, activity.length - actShown)} more
              </Button>
            </Stack>
          )}
        </>
      )}
    </Box>
  );
}
